<template>
    <div class="flight-passenger" v-suppress>
        <span class="flight-passenger-name">
            {{ name }}
            <span v-if="showGender"> ({{ genderLabel }}) </span>
            <span class="flight-passenger-dob" v-if="dateOfBirthLabel">
                {{ dateOfBirthLabel }}
            </span>
        </span>

        <span v-if="showBaggage" class="flight-passenger-baggages">
            <span v-for="(bag, bagIndex) in bagsIncluded.baggages" :key="bagIndex">
                <Icon v-if="bag.Type" :type="getIconByType(bag.Type)" />
                {{ getIncludedBaggageLabel(bag, bagsIncluded.splitSector, true) }}
                <Badge
                    :text="t({ key: 'checkout.included' })"
                    class="flight-passenger-baggages-badge"
                    color="green"
                    size="s" />
            </span>

            <span v-for="bag in selectedBaggages" :key="bag.Id">
                <Icon v-if="bag.Type" :type="getIconByType(bag.Type)" />
                {{ getBaggageLabel(bag, pax.AdditionalBaggage.SplitSector) }}
            </span>
        </span>
        <span v-else>
            <Icon type="NoBag" />
            {{ t({ key: 'shared.no_baggage' }) }}
        </span>

        <span class="flight-passenger-seating" v-if="selectedSeats.length > 0">
            <span v-for="seat in selectedSeats" :key="seat.Id">
                <Icon type="Seat" /> {{ getSeatDescription(seat) }}
            </span>
        </span>
    </div>
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import {
        getBaggageLabel,
        getIconByType,
        getIncludedBaggageLabel,
        getSelectedBaggages,
    } from '@shared/helpers/baggage';
    import { getSeatDescription } from '@/helpers/seating';
    import { IncludedBaggages } from '@shared/interfaces/baggage';
    import { computed, defineComponent, PropType } from 'vue';
    import { useStore } from '@/store';
    import { capitalizeWords } from '@shared/utils/string';
    import { FlightPax, FlightProduct } from '@/api/Cart/cart.interfaces';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { getLocaleDigitDate } from '@shared/helpers/datetime/datetime.formatters';
    import { getAgeRange } from '@/api/Cart/cart.helpers';
    import { AgeRangeDefinitions, PassengerType } from '@/api/Checkout/checkout.interfaces';
    import Icon from '@ui/components/Icon/Icon.vue';
    import Badge from '@ui/components/Badge/Badge.vue';

    export default defineComponent({
        name: 'FlightModalDetailsPassenger',
        components: {
            Badge,
            Icon,
        },
        methods: {
            t,
            capitalizeWords,
            getBaggageLabel,
            getIncludedBaggageLabel,
            getSelectedBaggages,
            getIconByType,
            getSeatDescription,
        },
        props: {
            index: {
                type: Number,
                required: true,
            },
            bagsIncluded: {
                type: Object as PropType<IncludedBaggages>,
                required: true,
            },
            pax: {
                type: Object as PropType<FlightPax>,
                required: true,
            },
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const passengerType = computed<PassengerType>(() => {
                const ageDefinitions: AgeRangeDefinitions =
                    store.getters['content/getCheckoutAgeRangeDefinitions'];

                return getAgeRange(props.product, props.pax, ageDefinitions).Type;
            });

            const genderLabel = computed(() => {
                if (!showGender.value) {
                    return undefined;
                }
                return passengerType.value === PassengerType.Adult
                    ? t({
                          key: `shared.gender_titles.adult.${props.pax.Gender}`,
                      })
                    : t({
                          key: `shared.gender_titles.children.${props.pax.Gender}`,
                      });
            });

            const showGender = computed<boolean>(() =>
                Boolean(props.pax.Gender && props.pax.Gender > 0)
            );

            const showFullName = computed<boolean>(() =>
                Boolean(props.pax.FirstName && props.pax.LastName)
            );

            const showBaggage = computed<boolean>(
                () => passengerType.value !== PassengerType.Infant
            );

            const selectedBaggages = computed(() =>
                getSelectedBaggages(props.pax.AdditionalBaggage)
            );

            const name = computed(() =>
                showFullName.value
                    ? capitalizeWords(`${props.pax.FirstName} ${props.pax.LastName}`)
                    : `${t({ key: 'shared.traveller' })} ${props.index + 1}`
            );

            const selectedSeats = computed(() => props.pax?.SelectedSeats || []);

            const dateOfBirthLabel = computed(() =>
                props.pax.DateOfBirth
                    ? getLocaleDigitDate(convertToDate(props.pax.DateOfBirth))
                    : ''
            );

            return {
                name,
                showBaggage,
                showGender,
                showFullName,
                selectedSeats,
                dateOfBirthLabel,
                selectedBaggages,
                genderLabel,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Passenger.scss';
</style>
